
/**
 * MR CACTUS — Shipping Progress
 *
 * Barra de progreso para envío gratis por cantidad.
 * Compatible con cart-extras.liquid y Shopify Function v6.
 *
 * Solo informativo: el descuento real lo aplica Shopify.
 */

(() => {
  'use strict';

  const MAX_QTY = 1000000;
  const SELECTOR = '[data-shipping-progress]';

  /**
   * Identifica el primer escalón promocional.
   *
   * Ejemplos:
   * 1-430 / 10-180 / 20-175 => 10
   * 10-180 / 20-175         => 10
   */
  function firstVolumeMinimum(raw) {
    if (typeof raw !== 'string') return null;

    const tiers = raw.split(/\r?\n/).map((line) => {
      const match = /^\s*(\d+)\s*-\s*\$?\s*([\d,.]+)\s*$/.exec(line);
      if (!match) return null;

      const qty = Number(match[1]);
      const price = Number(match[2].replace(/,/g, ''));

      if (!Number.isSafeInteger(qty) || qty < 1 || qty > MAX_QTY) {
        return null;
      }

      if (!Number.isFinite(price) || price <= 0) {
        return null;
      }

      return { qty, price };
    }).filter(Boolean);

    if (!tiers.length) return null;

    const baseTiers = tiers.filter((tier) => tier.qty === 1);
    const promotionalTiers = tiers.filter((tier) => tier.qty > 1);

    if (!promotionalTiers.length) return null;

    if (baseTiers.length) {
      const basePrice = baseTiers[0].price;

      const discountedTiers = promotionalTiers.filter(
        (tier) => tier.price < basePrice
      );

      return discountedTiers.length
        ? Math.min(...discountedTiers.map((tier) => tier.qty))
        : null;
    }

    return Math.min(...promotionalTiers.map((tier) => tier.qty));
  }

  /**
   * Replica shippingProgress() de la Shopify Function.
   * Solo cuenta las líneas que Liquid marcó como elegibles.
   */
  function calculate(root) {
    let count = 0;
    let weighted = 0;

    root.querySelectorAll('[data-shipping-line]').forEach((line) => {
      const qty = Number(line.dataset.quantity);

      if (
        !Number.isSafeInteger(qty) ||
        qty <= 0 ||
        qty > MAX_QTY
      ) {
        return;
      }

      const tierMin = firstVolumeMinimum(line.dataset.volume);

      // Atributo ausente/vacío significa que no hay mínimo manual.
      const manualRaw = line.dataset.manual;
      const fallback = Number(manualRaw);

      const manualMin =
        manualRaw != null &&
        manualRaw.trim() !== '' &&
        Number.isSafeInteger(fallback) &&
        fallback > 0 &&
        fallback <= MAX_QTY
          ? fallback
          : null;

      const min = tierMin !== null ? tierMin - 1 : manualMin;

      // Configuración incompleta: no conceder progreso ficticio.
      if (min === null || min <= 0) return;

      count += qty;
      weighted += qty * min;
    });

    if (!count) return null;

    const target = Math.ceil(weighted / count);
    if (!Number.isFinite(target) || target <= 0) return null;

    return {
      count,
      target,
      remaining: Math.max(0, target - count),
      qualified: count >= target,
      percent: Math.min(100, Math.round((count / target) * 100))
    };
  }

  /**
   * Actualiza texto, accesibilidad y barra visual.
   */
  function render(root) {
    const data = calculate(root);

    if (!data) {
      root.hidden = true;
      return;
    }

    const title = root.querySelector('[data-shipping-title]');
    const status = root.querySelector('[data-shipping-status]');
    const counter = root.querySelector('[data-shipping-counter]');
    const bar = root.querySelector('[data-shipping-bar]');
    const fill = root.querySelector('[data-shipping-fill]');

    root.hidden = false;
    root.classList.toggle('is-qualified', data.qualified);

    if (title) {
      title.textContent = data.qualified
        ? '¡Felicidades! Desbloqueaste el envío GRATIS'
        : '¡Tu envío GRATIS está cada vez más cerca!';
    }

    if (status) {
      status.textContent = data.qualified
        ? 'Tu carrito alcanzó el mínimo de piezas elegibles.'
        : `Agrega ${data.remaining} ${
            data.remaining === 1 ? 'pieza' : 'piezas'
          } más para obtener envío gratis.`;
    }

    if (counter) {
      counter.textContent =
        `${data.count} de ${data.target} piezas elegibles`;
    }

    if (bar) {
      bar.setAttribute('aria-valuenow', String(data.percent));
      bar.setAttribute(
        'aria-valuetext',
        `${data.count} de ${data.target} piezas elegibles`
      );
    }

    if (fill) {
      fill.style.width = `${data.percent}%`;
    }

    // El panel flotante se posiciona según su altura.
    // Avisamos al tema para que recalcule su posición.
    if (root.closest('[data-added]')) {
      window.dispatchEvent(new Event('resize'));
    }
  }

  /**
   * Busca y actualiza las barras dentro de un contenedor.
   */
  function refresh(scope = document) {
    if (scope.matches?.(SELECTOR)) {
      render(scope);
    }

    scope.querySelectorAll?.(SELECTOR).forEach(render);
  }

  /**
   * Inicializa barras y detecta contenido nuevo.
   *
   * theme.js solicita /cart?section_id=cart-extras y
   * después inserta el HTML en el panel flotante.
   */
  function init() {
    refresh();

    const observer = new MutationObserver((mutations) => {
      const pending = new Set();

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;

          if (node.matches(SELECTOR)) {
            pending.add(node);
          }

          node.querySelectorAll(SELECTOR).forEach((root) => {
            pending.add(root);
          });
        }
      }

      pending.forEach(render);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Permite actualizar manualmente cuando el tema
    // cambie cantidades sin reemplazar todo el HTML.
    document.addEventListener('cart:updated', () => {
      refresh();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
